/**
 * 品牌创建/编辑弹窗
 *
 * MUI Dialog 表单，用于创建新品牌或编辑已有品牌信息。
 * 可选填写品牌通用口令（access_code），留空时创建自动生成、编辑则不修改。
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Autocomplete,
  Chip,
  MenuItem,
} from '@mui/material'
import type { Brand } from '@chaoyinli/shared'
import type { BrandCreateParams, BrandUpdateParams } from '@/api/services'
import dayjs from 'dayjs'

interface BrandFormDialogProps {
  /** 是否打开 */
  open: boolean
  /** 关闭回调 */
  onClose: () => void
  /** 提交回调（创建或更新） */
  onSubmit: (data: BrandCreateParams | BrandUpdateParams) => Promise<void>
  /** 编辑时传入的品牌数据，为空则为创建模式 */
  brand?: Brand | null
  /** 是否正在提交 */
  submitting?: boolean
}

/** 品类建议项（仅作为提示，用户可自由输入任意品类） */
const CATEGORY_SUGGESTIONS = ['餐饮', '茶饮', '烘焙', '火锅', '烧烤', '快餐', '日料', '西餐', '其他']

/** 品类分隔符：英文逗号、中文逗号、顿号、斜杠、竖线、分号 */
const CATEGORY_SPLIT_REGEX = /[,，、/|;；\s]+/

/**
 * 将后端返回的品类字符串拆分为数组（兼容历史数据 "小龙虾/夜宵"）。
 * @param raw 原始品类字符串
 * @returns 去重后的品类数组，空值返回 []
 */
function parseCategories(raw: string | null | undefined): string[] {
  if (!raw) return []
  const parts = String(raw).trim().split(CATEGORY_SPLIT_REGEX)
  const result: string[] = []
  parts.forEach((part) => {
    const item = part.trim()
    if (item && !result.includes(item)) {
      result.push(item)
    }
  })
  return result
}

/**
 * 将品类数组拼回逗号分隔字符串，用于提交后端。
 * @param list 品类数组
 * @returns 逗号分隔字符串，空数组返回 ''
 */
function joinCategories(list: string[]): string {
  return list
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .join(',')
}

export function BrandFormDialog({
  open,
  onClose,
  onSubmit,
  brand,
  submitting = false,
}: BrandFormDialogProps) {
  const isEdit = !!brand

  const [name, setName] = useState('')
  const [subBrand, setSubBrand] = useState('')
  // 品类支持多选 + 自由输入，内部以数组维护，提交时 join 成逗号分隔字符串
  const [categories, setCategories] = useState<string[]>([])
  const [slogan, setSlogan] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [trialDays, setTrialDays] = useState('7')
  const [quotaTotal, setQuotaTotal] = useState('0')
  const [unitPrice, setUnitPrice] = useState('0')
  const [trialStart, setTrialStart] = useState('')
  const [trialEnd, setTrialEnd] = useState('')
  const [accessCode, setAccessCode] = useState('')
  const [error, setError] = useState('')

  /** 将后端日期字符串格式化为 YYYY-MM-DD，供 input[type=date] 使用 */
  const toDateInputValue = (date: string | null | undefined): string => {
    if (!date) return ''
    const d = dayjs(date)
    return d.isValid() ? d.format('YYYY-MM-DD') : ''
  }

  // 品牌数据变化时重置表单
  useEffect(() => {
    if (open) {
      if (brand) {
        setName(brand.name || '')
        setSubBrand(brand.sub_brand || '')
        setCategories(parseCategories(brand.category))
        setSlogan(brand.slogan || '')
        setLogoUrl(brand.logo_url || '')
        setQuotaTotal(String(brand.quota_total ?? 0))
        setUnitPrice(((brand.unit_price ?? 0) / 100).toString())
        setTrialStart(toDateInputValue(brand.trial_start))
        setTrialEnd(toDateInputValue(brand.trial_end))
        setTrialDays('7')
        // 出于安全考虑，编辑时不回显明文口令，仅允许留空（不修改）或填写新值
        setAccessCode('')
      } else {
        setName('')
        setSubBrand('')
        setCategories([])
        setSlogan('')
        setLogoUrl('')
        setTrialDays('7')
        setQuotaTotal('0')
        setUnitPrice('0')
        setAccessCode('')
      }
      setError('')
    }
  }, [open, brand])

  const handleSubmit = async () => {
    // 表单验证
    if (!name.trim()) {
      setError('请输入品牌名称')
      return
    }

    setError('')

    // access_code：仅当填写时才提交，留空则交由后端处理（创建自动生成 / 编辑不修改）
    const accessCodeValue = accessCode.trim() || undefined

    // 多品类：数组 join 成逗号分隔字符串存回 category
    const categoryValue = joinCategories(categories) || undefined

    if (isEdit) {
      // 编辑模式：只提交与原始值不同的字段，清空日期时传 null
      const changed = <T extends string | null | undefined>(
        current: string,
        original: T
      ): string | null | undefined => {
        const normalizedOriginal = toDateInputValue(original as string)
        if (current === normalizedOriginal) return undefined
        return current || null
      }

        const data: BrandUpdateParams = {
          name: name.trim(),
          sub_brand: subBrand.trim() || undefined,
          category: categoryValue,
          slogan: slogan.trim() || undefined,
          logo_url: logoUrl.trim() || undefined,
          quota_total: Number(quotaTotal) || 0,
          unit_price: Math.round((Number(unitPrice) || 0) * 100),
          trial_start: changed(trialStart, brand?.trial_start),
          trial_end: changed(trialEnd, brand?.trial_end),
          access_code: accessCodeValue,
        }
      await onSubmit(data)
    } else {
      // 创建模式
      const data: BrandCreateParams = {
        name: name.trim(),
        sub_brand: subBrand.trim() || undefined,
        category: categoryValue,
        slogan: slogan.trim() || undefined,
        logo_url: logoUrl.trim() || undefined,
        trial_days: Number(trialDays) || 7,
        access_code: accessCodeValue,
      }
      await onSubmit(data)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '18px' }}>
        {isEdit ? '编辑品牌信息' : '创建新品牌'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="品牌名称"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!error}
            helperText={error}
            size="small"
            fullWidth
          />
          <TextField
            label="子品牌"
            value={subBrand}
            onChange={(e) => setSubBrand(e.target.value)}
            size="small"
            fullWidth
            placeholder="可选"
          />
          <Autocomplete
            multiple
            freeSolo
            options={CATEGORY_SUGGESTIONS}
            value={categories}
            onChange={(_, newValue) => {
              // newValue 可能包含用户自由输入的字符串，统一清洗 + 去重
              const cleaned: string[] = []
              ;(newValue as string[]).forEach((item) => {
                // 支持一次粘贴 "小龙虾,夜宵" 这类多值文本
                parseCategories(item).forEach((c) => {
                  if (!cleaned.includes(c)) cleaned.push(c)
                })
              })
              setCategories(cleaned)
            }}
            renderTags={(value: string[], getTagProps) =>
              value.map((option: string, index: number) => {
                const { key, ...tagProps } = getTagProps({ index })
                return (
                  <Chip
                    key={key}
                    label={option}
                    size="small"
                    {...tagProps}
                    sx={{ backgroundColor: '#FFF3E0', color: '#E67E00' }}
                  />
                )
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="品类"
                size="small"
                fullWidth
                placeholder={categories.length === 0 ? '可多选，也可直接输入自定义品类' : ''}
                helperText="支持多选与自由输入，回车确认；多个品类都会被 AI 参考"
              />
            )}
          />
          <TextField
            label="Slogan"
            value={slogan}
            onChange={(e) => setSlogan(e.target.value)}
            size="small"
            fullWidth
            placeholder="品牌口号"
          />
          <TextField
            label="Logo URL"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            size="small"
            fullWidth
            placeholder="https://..."
          />
          <TextField
            label="品牌通用口令"
            value={accessCode}
            onChange={(e) => setAccessCode(e.target.value)}
            size="small"
            fullWidth
            placeholder={isEdit ? '留空则不修改' : '留空则自动生成（8-12 位字母数字）'}
            helperText="员工登录所用口令，8-12 位字母或数字"
          />
          {isEdit ? (
            <>
              <TextField
                label="总点数额度池"
                type="number"
                value={quotaTotal}
                onChange={(e) => setQuotaTotal(e.target.value)}
                size="small"
                fullWidth
                helperText="该品牌所有员工共用的总生成点数，用完即止"
              />
              <TextField
                label="单点金额（元）"
                type="number"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                size="small"
                fullWidth
                helperText="每次生成记录的单价（元），仅作记录不直接扣费"
              />
            </>
          ) : (
            <TextField
              label="试用天数"
              type="number"
              value={trialDays}
              onChange={(e) => setTrialDays(e.target.value)}
              size="small"
              fullWidth
              helperText="默认 7 天"
            />
          )}
          {isEdit && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="试用开始"
                type="date"
                value={trialStart}
                onChange={(e) => setTrialStart(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="留空表示不设置"
              />
              <TextField
                label="试用截止"
                type="date"
                value={trialEnd}
                onChange={(e) => setTrialEnd(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="决定试用状态是否过期"
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: '#666' }}>
          取消
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            backgroundColor: '#FF8C00',
            '&:hover': { backgroundColor: '#E67E00' },
          }}
        >
          {submitting ? '提交中...' : isEdit ? '保存' : '创建'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default BrandFormDialog
